import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { Alert, Box, Button, Heading, Paragraph } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { LoadingButton, LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';
import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyMultiplexWebApiHandler_create,
  useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { MAX_NUM_MULTIPLEX_UNITS_VALUE } from '../../constants';

import UnitOverview from './components/UnitOverview';

import type {
  UserPropertyMultiplexCreateUnitRequestWebDto,
  UserPropertyMultiplexCreateUnitResponseWebDto,
  UserPropertyMultiplexCreateUnitsRequestWebDto,
  UserPropertyMultiplexCreateUnitsResponseWebDto,
  UserPropertyMultiplexGetUnitNumbersResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

type Props = {
  customerId?: string;
  fullAddress: string;
  numUnits?: string;
  onSuccess?: ({
    multiplexBuildingSessionId,
    unitsCreated,
    hasPartialUnitsSucceeded,
  }: {
    multiplexBuildingSessionId: string;
    unitsCreated: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
    hasPartialUnitsSucceeded: boolean;
  }) => void;
  onErrorChange?: ({ hasError, message }: { hasError?: boolean | undefined; message?: string | undefined }) => void;
  onCreateStart?: () => void;
};

export type MultiplexUnitEntryRef = {
  createMultiplex: () => void;
};

const MAX_UNITS = MAX_NUM_MULTIPLEX_UNITS_VALUE;

const initialData = {
  0: {
    unitNumber: '',
    bedrooms: '',
    baths: '',
  },
  1: {
    unitNumber: '',
    bedrooms: '',
    baths: '',
  },
  2: {
    unitNumber: '',
    bedrooms: '',
    baths: '',
  },
};

const getInitialUnitData = (numUnits?: string) => {
  if (!numUnits) {
    return initialData;
  }

  // ensure the number of units is between 1 and 24
  const count = Math.min(Math.max(Number(numUnits) || 3, 1), MAX_UNITS);
  const data: Record<string, { unitNumber: string; bedrooms: string; baths: string }> = {};

  for (let i = 0; i < count; i++) {
    data[i] = {
      unitNumber: '',
      bedrooms: '',
      baths: '',
    };
  }

  return data;
};

/**
 * forwardRef<T, P>
 *   T - MultiplexUnitEntryRef - The type of the DOM node.
 *   P - Props - The props the component accepts
 */
const MultiplexUnitEntry = forwardRef<MultiplexUnitEntryRef, Props>(
  ({ customerId, fullAddress, numUnits, onErrorChange, onSuccess, onCreateStart }, ref) => {
    const queryClient = useQueryClient();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [unitOverviewList, setUnitOverviewList] = useState<
      Record<string, { unitNumber: string; bedrooms: string; baths: string }>
    >(getInitialUnitData(numUnits));

    // field flags passed to UnitOverview for inline error messages (updated on change)
    const [errorMap, setErrorMap] = useState<
      Record<
        string,
        {
          hasDuplicateUnitNumber?: boolean | undefined;
          hasEmptyUnitNumber?: boolean | undefined;
          hasMaxCharactersUnitNumber?: boolean | undefined;
          hasEmptyBedrooms?: boolean | undefined;
          hasEmptyBaths?: boolean | undefined;
        }
      >
    >({});

    const {
      data: unitNumbersResponse = {} as unknown as UserPropertyMultiplexGetUnitNumbersResponseWebDto,
      error: unitNumbersError = {},
      isError: isErrorUnitNumbers,
      isLoading: isLoadingUnitNumbers,
    } = useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers({
      fullAddress,
    });

    // Derived from unitOverviewList — used by getHasErrors for submit disable (not errorMap empty flags).
    const hasZeroUnitOverviewItems = useMemo(() => Object.keys(unitOverviewList).length === 0, [unitOverviewList]);
    const hasOneUnitOverviewItem = useMemo(() => Object.keys(unitOverviewList).length === 1, [unitOverviewList]);
    const hasReachedMaxUnits = useMemo(() => Object.keys(unitOverviewList).length >= MAX_UNITS, [unitOverviewList]);

    const hasEmptyUnitNumbers = useMemo(
      () => Object.values(unitOverviewList).some((unit) => unit.unitNumber.trim() === ''),
      [unitOverviewList],
    );

    const hasEmptyBedrooms = useMemo(
      () => Object.values(unitOverviewList).some((unit) => unit.bedrooms.trim() === ''),
      [unitOverviewList],
    );

    const hasEmptyBaths = useMemo(
      () => Object.values(unitOverviewList).some((unit) => unit.baths.trim() === ''),
      [unitOverviewList],
    );

    const unitNumbersList = useMemo(() => unitNumbersResponse?.unitNumbers ?? [], [unitNumbersResponse?.unitNumbers]);

    const { mutate: createMultiplexMutation } = useRentalManagerUserPropertyMultiplexWebApiHandler_create({});

    const getHasErrors = useCallback(() => {
      // Empty required fields: unitOverviewList. Duplicate/max-length: errorMap. Empty flags in errorMap are UI-only.
      return (
        Object.values(errorMap).some((error) => error.hasDuplicateUnitNumber ?? false) ||
        Object.values(errorMap).some((error) => error.hasMaxCharactersUnitNumber ?? false) ||
        hasZeroUnitOverviewItems ||
        hasEmptyUnitNumbers ||
        hasEmptyBedrooms ||
        hasEmptyBaths
      );
    }, [errorMap, hasZeroUnitOverviewItems, hasEmptyUnitNumbers, hasEmptyBedrooms, hasEmptyBaths]);

    /**
     * expose methods to the parent component
     */
    useImperativeHandle(ref, () => ({
      createMultiplex,
    }));

    useEffect(() => {
      if (isErrorUnitNumbers) {
        const errorMessage = DatarouterError.getDisplayMessage(unitNumbersError);

        setErrorMessage(errorMessage);
        onErrorChange?.({ hasError: true, message: errorMessage });
        return;
      }

      if (unitNumbersResponse) {
        checkForExistingUnits(unitNumbersResponse.unitNumbers ?? []);
      }
    }, [isLoadingUnitNumbers, isErrorUnitNumbers, unitNumbersError, unitNumbersResponse, unitNumbersList]);

    useEffect(() => {
      // notify parent when getHasErrors changes
      onErrorChange?.({ hasError: getHasErrors() });
    }, [getHasErrors, onErrorChange]);

    const checkForExistingUnits = (unitNumbers: Array<string>) => {
      if (unitNumbers.length > 0) {
        // Format: "101, 102, and 103"
        const unitListString =
          unitNumbers.length > 1
            ? `${unitNumbers.slice(0, -1).join(', ')} and ${unitNumbers[unitNumbers.length - 1]}`
            : unitNumbers[0];

        setAlertMessage(`Units <strong>${unitListString}</strong> already exist at this property.`);
      }
    };

    const clearError = () => {
      setErrorMessage(null);
      onErrorChange?.({ hasError: false, message: undefined });
    };

    const createMultiplex = () => {
      clearError();
      onCreateStart?.();

      // Convert unitOverviewList Record to Array with proper types
      const units: Array<UserPropertyMultiplexCreateUnitRequestWebDto> = Object.values(unitOverviewList).map(
        (unit) => ({
          unitNumber: unit.unitNumber,
          // if unit.bedrooms = 'studio' then set bedroomCount to 0
          bedroomCount: unit.bedrooms === 'studio' ? 0 : unit.bedrooms ? Number(unit.bedrooms) : undefined,
          bathroomCount: unit.baths ? Number(unit.baths) : undefined,
        }),
      );

      const postData: UserPropertyMultiplexCreateUnitsRequestWebDto = {
        // only include customerId if it is provided
        ...(customerId && { customerId }),
        fullAddress,
        units,
      };

      createMultiplexMutation(
        { postData },
        {
          onSuccess: (response: UserPropertyMultiplexCreateUnitsResponseWebDto) => {
            // invalidate properties query key after create multiplex
            queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

            // extract the array of units from the response
            const { multiplexBuildingSessionId, units } =
              response ?? ({} as UserPropertyMultiplexCreateUnitsResponseWebDto);

            // set flags for partial success and all units failed
            const failedUnits = units.filter((unit) => !unit.success);
            const successfulUnits = units.filter((unit) => unit.success);
            const allUnitsFailed = units.every((unit) => !unit.success);
            const hasPartialUnitsSucceeded = successfulUnits.length > 0 && failedUnits.length > 0;

            // if all units failed
            if (allUnitsFailed) {
              // format error messages for failed units
              const errorMessages = failedUnits
                .map((unit) => `Unit ${unit.unitNumber}: ${unit.errorMessage}`)
                .join(' ');

              setErrorMessage(errorMessages);
              onErrorChange?.({ hasError: true });

              return;
            }

            // if partial units succeeded or all units succeeded, proceed to the next step
            clearError();
            onSuccess?.({
              multiplexBuildingSessionId,
              unitsCreated: successfulUnits,
              hasPartialUnitsSucceeded,
            });
          },
          onError: (error) => {
            const errorMessage = DatarouterError.getDisplayMessage(error);

            setErrorMessage(errorMessage);
            onErrorChange?.({ hasError: true, message: errorMessage });
          },
        },
      );
    };

    const handleUnitOverviewChange = (index: string, name: string, value: string) => {
      // Update the unit data
      setUnitOverviewList((prev) => ({
        ...prev,
        [index]: { ...prev[index], [name]: value },
      }));

      // update errorMap for inline UnitOverview messages (including empty-field flags)
      setErrorMap((prev) => {
        const currentErrors = prev[index] ?? {};

        if (name === 'unitNumber') {
          return {
            ...prev,
            [index]: {
              ...currentErrors,
              hasDuplicateUnitNumber: unitNumbersList.includes(value),
              hasEmptyUnitNumber: value.trim() === '',
              hasMaxCharactersUnitNumber: value.length > 20,
            },
          };
        }

        if (name === 'bedrooms') {
          return {
            ...prev,
            [index]: {
              ...currentErrors,
              hasEmptyBedrooms: value.trim() === '',
            },
          };
        }

        if (name === 'baths') {
          return {
            ...prev,
            [index]: {
              ...currentErrors,
              hasEmptyBaths: value.trim() === '',
            },
          };
        }

        return prev;
      });
    };

    const handleUnitOverviewDelete = (index: string) => {
      setUnitOverviewList((prev) => {
        const updated = { ...prev };
        delete updated[index];

        return updated;
      });

      setErrorMap((prev) => {
        const { [index]: _deleted, ...rest } = prev;

        return rest;
      });
    };

    const handleAddUnit = () => {
      setUnitOverviewList((prev) => {
        // Check if we've already reached the maximum number of units
        if (Object.keys(prev).length >= MAX_UNITS) {
          return prev;
        }

        // add a new unit with a unique incrementing numeric key.
        const keys = Object.keys(prev).map(Number);
        const maxKey = keys.length > 0 ? Math.max(...keys) : -1;
        const newKey = String(maxKey + 1);

        return {
          ...prev,
          [newKey]: {
            unitNumber: '',
            bedrooms: '',
            baths: '',
          },
        };
      });
    };

    const renderSkeletonLoadingState = () => {
      return (
        <>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              css={{
                padding: '300',
              }}
            >
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '400',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  marginBottom: '400',
                }}
              >
                {/* Unit Number Field */}
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
                  <LoadingParagraph isLoading={true} loadingWidth="80px" textStyle="body-sm">
                    &nbsp;
                  </LoadingParagraph>
                  <LoadingText isLoading={true} loadingWidth="100%" textStyle="heading-md">
                    &nbsp;
                  </LoadingText>
                  <Box css={{ height: '21.5px' }} />
                </Box>

                {/* Bedrooms Field */}
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
                  <LoadingParagraph isLoading={true} loadingWidth="80px" textStyle="body-sm">
                    &nbsp;
                  </LoadingParagraph>
                  <LoadingText isLoading={true} loadingWidth="100%" textStyle="heading-md">
                    &nbsp;
                  </LoadingText>
                  <Box css={{ height: '21.5px' }} />
                </Box>

                {/* Baths Field */}
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
                  <LoadingParagraph isLoading={true} loadingWidth="80px" textStyle="body-sm">
                    &nbsp;
                  </LoadingParagraph>
                  <LoadingText isLoading={true} loadingWidth="100%" textStyle="heading-md">
                    &nbsp;
                  </LoadingText>
                  <Box css={{ height: '21.5px' }} />
                </Box>

                {/* Delete Button */}
                <LoadingButton isLoading={true} loadingWidth="20px" css={{ marginBottom: '400' }}>
                  &nbsp;
                </LoadingButton>
              </Box>
            </Box>
          ))}
        </>
      );
    };

    return (
      <Box
        data-testid="MultiplexUnitEntry"
        css={{
          paddingLeft: { base: '0', lg: '1200' },
          paddingRight: { base: '0', lg: '1200' },
          paddingTop: '400',
          paddingBottom: '400',
        }}
      >
        <Box data-testid="TitleAndIntro" css={{ marginBottom: '600' }}>
          <Heading level={4} css={{ marginBottom: '200' }}>
            Tell us about your units
          </Heading>

          <Paragraph textStyle="body">
            Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your
            perfect renter.
          </Paragraph>

          {errorMessage && (
            <Alert data-testid="ErrorAlert" role="status" tone="info" css={{ marginTop: '200' }}>
              {errorMessage}
            </Alert>
          )}

          {alertMessage && (
            <Alert data-testid="AlertMessage" role="status" tone="info" css={{ marginTop: '200' }}>
              <span dangerouslySetInnerHTML={{ __html: alertMessage }} />
            </Alert>
          )}
        </Box>

        <Box
          data-testid="UnitOverviewList-Container"
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box data-testid="UnitOverviewList">
            {isLoadingUnitNumbers ? (
              renderSkeletonLoadingState()
            ) : (
              <>
                {Object.keys(unitOverviewList).map((index) => {
                  const unit = unitOverviewList[index];
                  const errors = errorMap[index];

                  return (
                    <UnitOverview
                      key={index}
                      index={Number(index)}
                      unitNumber={unit?.unitNumber ?? ''}
                      bedrooms={unit?.bedrooms ?? ''}
                      baths={unit?.baths ?? ''}
                      hasDuplicateUnitNumber={errors?.hasDuplicateUnitNumber}
                      hasEmptyUnitNumber={errors?.hasEmptyUnitNumber}
                      hasMaxCharactersUnitNumber={errors?.hasMaxCharactersUnitNumber}
                      hasEmptyBedrooms={errors?.hasEmptyBedrooms}
                      hasEmptyBaths={errors?.hasEmptyBaths}
                      disableDeleteButton={hasOneUnitOverviewItem}
                      onChange={handleUnitOverviewChange}
                      onDelete={handleUnitOverviewDelete}
                    />
                  );
                })}
              </>
            )}
          </Box>

          <Button
            fluid={true}
            tone="brand"
            emphasis="outlined"
            icon={<IconPlusFilled />}
            onClick={handleAddUnit}
            disabled={hasReachedMaxUnits}
            css={{
              alignSelf: 'center',
            }}
          >
            Add unit
          </Button>
        </Box>
      </Box>
    );
  },
);

MultiplexUnitEntry.displayName = 'MultiplexUnitEntry';

export default MultiplexUnitEntry;
